import {
  type MemoryKindValue,
  type MemoryProvenanceValue,
  type MemoryReviewReasonValue,
  type MemoryStaffReasonCodeValue,
  type MemoryStatusValue,
  memoryApiLabelKey,
  memoryKindLabelKey,
  memoryKindValues,
  memoryStaffReasonCodeValues,
  memoryVocabularyCodesV1,
  type StaffMemoryFact,
} from "@lafayette/schemas";

import type { Option } from "../catalog/labels";

// The English words of the memory pages. The API sends i18n KEYS for a vocabulary value
// (`memory.dietary.vegetarian`) and a kind heading (`memory.kind.dietary_preference`); the console owns the
// wording (`memoryApiLabelKey`, `memoryKindLabelKey`). A test checks that every key of the closed
// vocabulary has a label here, so a new code can never show as a raw key.

const humanize = (code: string): string => {
  const words = code.replaceAll("_", " ");
  return `${words.charAt(0).toUpperCase()}${words.slice(1)}`;
};

const KIND_LABELS: Readonly<Record<MemoryKindValue, string>> = {
  DIETARY_PREFERENCE: "Dietary preferences",
  DINING_STYLE: "Dining style",
  PROPERTY_PREFERENCE: "Stay preferences",
  SCHEDULE_PREFERENCE: "Schedule preferences",
  INTEREST: "Interests",
  DISLIKED_EXPERIENCE: "Dislikes",
  TRAVEL_COMPANION_PATTERN: "Who they travel with",
  EXPLICIT_INSTRUCTION: "Standing instructions",
};

// Where the plain "capital letter, then spaces" reading of a code is not right.
const CODE_OVERRIDES: Readonly<Record<string, string>> = {
  chefs_table: "Chef's table",
  with_pet: "Travels with a pet",
  no_calls_before_10: "No calls before 10 a.m.",
};

const LABELS: ReadonlyMap<string, string> = new Map([
  ...memoryKindValues.map((kind): [string, string] => [
    memoryKindLabelKey(kind),
    KIND_LABELS[kind],
  ]),
  ...memoryKindValues.flatMap((kind) =>
    memoryVocabularyCodesV1[kind].map((code): [string, string] => [
      memoryApiLabelKey(kind, code),
      CODE_OVERRIDES[code] ?? humanize(code),
    ]),
  ),
]);

/** The words for an i18n key of the memory API, or `null` when this console does not know the key. */
export const labelForKey = (key: string): string | null => LABELS.get(key) ?? null;

export const kindLabel = (kind: MemoryKindValue): string => KIND_LABELS[kind];

/** The label of a code of the closed vocabulary (`gluten_free`: "Gluten free"). */
export const codeLabel = (kind: MemoryKindValue, code: string): string =>
  LABELS.get(memoryApiLabelKey(kind, code)) ?? humanize(code);

export const kindOptions: readonly Option<MemoryKindValue>[] = memoryKindValues.map((kind) => ({
  value: kind,
  label: KIND_LABELS[kind],
}));

export const codeOptions = (kind: MemoryKindValue): readonly Option[] =>
  memoryVocabularyCodesV1[kind].map((code) => ({ value: code, label: codeLabel(kind, code) }));

// --- a fact -----------------------------------------------------------------------------------------

export const ERASED_VALUE_LABEL = "Value erased";

/**
 * What a fact says. A deleted fact has no value (`null`): the value is erased and only the history stays.
 * A CODE value shows its vocabulary label; a TEXT value shows the text as written, as text only.
 */
export function factValueLabel(
  fact: Pick<StaffMemoryFact, "kind" | "value" | "labelKey" | "form">,
): string {
  if (fact.value === null) return ERASED_VALUE_LABEL;
  if (fact.form === "TEXT") return fact.value;
  return (fact.labelKey === null ? null : labelForKey(fact.labelKey)) ?? humanize(fact.value);
}

export const memoryStatusLabels: Readonly<Record<MemoryStatusValue, string>> = {
  CANDIDATE: "Needs review",
  ACTIVE: "Active",
  DISPUTED: "Disputed",
  SUPERSEDED: "Replaced",
  EXPIRED: "Expired",
  DELETED: "Deleted",
};

/**
 * How a fact came to be, in staff words. The inferred fact says so in its badge and in its sentence: a
 * guess of the assistant is never shown as something the customer declared.
 */
export const provenanceLabels: Readonly<
  Record<MemoryProvenanceValue, Readonly<{ badge: string; detail: string; certain: boolean }>>
> = {
  CUSTOMER_CONFIRMED: {
    badge: "Confirmed by the customer",
    detail: "The customer added or confirmed this themselves in the app.",
    certain: true,
  },
  STAFF_CONFIRMED: {
    badge: "Confirmed by staff",
    detail: "A staff member entered or confirmed this.",
    certain: true,
  },
  CUSTOMER_STATED_IN_CHAT: {
    badge: "Said by the customer in chat",
    detail: "The customer said this in a chat. No person has confirmed it yet.",
    certain: false,
  },
  MODEL_INFERRED: {
    badge: "Inferred by the assistant",
    detail:
      "The assistant guessed this from the conversation. The customer did not state it. Treat it as unverified until a person confirms it.",
    certain: false,
  },
};

export const confirmationLabels = {
  UNCONFIRMED: "Not confirmed by a person",
  STAFF_CONFIRMED: "Confirmed by staff",
  CUSTOMER_CONFIRMED: "Confirmed by the customer",
} as const;

export const sourceTypeLabels = {
  EXPLICIT_CUSTOMER_STATEMENT: "Customer statement",
  EXPLICIT_REMEMBER_REQUEST: "Customer asked us to remember",
  STAFF_ENTRY: "Staff entry",
  APPROVED_EXTRACTION: "Extracted from a chat",
  HISTORICAL_TRIP_OUTCOME: "Outcome of a trip",
  SYSTEM_DERIVED_OBSERVATION: "System observation",
} as const;

export const sourceReferenceKindLabels = {
  CONVERSATION_MESSAGE: "Chat message",
  CONCIERGE_REQUEST: "Concierge request",
  TRIP: "Trip",
  EXTRACTION_RUN: "Extraction run",
  CUSTOMER_APP_ENTRY: "Customer app entry",
  STAFF_CONSOLE_ENTRY: "Staff console entry",
} as const;

/** What the source of a fact is, in words. The id of the thing is not shown: staff have no page to follow it to. */
export const sourceReferenceDescriptions = {
  CONVERSATION_MESSAGE: "A message in the conversation",
  CONCIERGE_REQUEST: "A concierge request",
  TRIP: "A trip",
  EXTRACTION_RUN: "A memory extraction run",
  CUSTOMER_APP_ENTRY: "An entry the customer made in the app",
  STAFF_CONSOLE_ENTRY: "An entry a staff member made in the console",
} as const;

export const sensitivityLabels = {
  STANDARD: "Standard",
  PERSONAL: "Personal",
  SENSITIVE: "Sensitive (older record, staff only)",
} as const;

export const formLabels = {
  CODE: "Standard choice",
  TEXT: "Free text",
} as const;

export const reviewReasonLabels: Readonly<Record<MemoryReviewReasonValue, string>> = {
  INFERRED: "The assistant inferred it",
  PERSONAL_NEEDS_REMEMBER: "A personal detail the customer did not ask us to remember",
  KIND_REQUIRES_REVIEW: "This kind of memory always needs a review",
  FREE_TEXT: "Free text always needs a review",
  LOW_CONFIDENCE: "The assistant was not confident",
  RECENT_CUSTOMER_DELETION: "The customer removed a similar memory recently",
  UPDATE_PROPOSED: "Proposes a change to an existing memory",
  CONFLICT: "Conflicts with an existing memory",
  CONFLICTS_WITH_CANDIDATE: "Conflicts with another candidate",
  SCOPE_AMBIGUOUS: "Unclear if it is lasting or only for one trip",
};

export const reasonCodeLabels: Readonly<Record<MemoryStaffReasonCodeValue, string>> = {
  CUSTOMER_REQUESTED: "The customer asked for this",
  CUSTOMER_CONFIRMED_VERBALLY: "The customer confirmed it by phone or in person",
  VERIFIED_FROM_TRIP: "Verified from a trip",
  INCORRECT_EXTRACTION: "The assistant got it wrong",
  OUTDATED: "No longer true",
  DUPLICATE: "Duplicate of another memory",
  NOT_ALLOWED_SENSITIVE: "Should not be stored (sensitive)",
  CONFLICT_RESOLVED: "A conflict was resolved",
  OTHER: "Another reason",
};

/** The reason select. The list is closed: no free text goes into a reason (trust contract, channel L7). */
export const reasonOptions: readonly Option<MemoryStaffReasonCodeValue>[] =
  memoryStaffReasonCodeValues.map((value) => ({ value, label: reasonCodeLabels[value] }));

export const relationshipLabels = {
  PRIMARY: "Primary concierge",
  BACKUP: "Backup concierge",
  TEMPORARY: "Temporary cover",
  SUPERVISOR: "Supervisor access",
} as const;

export const actorKindLabels = {
  customer: "Customer",
  staff: "Staff",
  system: "System",
} as const;

export const evidenceEventLabels = {
  ORIGIN: "First evidence",
  REINFORCE: "Said again",
  RETRACTION: "Taken back",
  SOURCE_EDITED: "Source edited",
  SOURCE_DELETED: "Source deleted",
} as const;

/** Who wrote the chat message of an evidence row (CHT-1984), in the words of a sentence: "A message from the customer". */
export const evidenceMessageAuthorLabels = {
  CUSTOMER: "the customer",
  LAFAYETTE_AI: "the assistant",
  CONCIERGE: "a concierge",
  SYSTEM: "the system",
} as const;

export const runStatusLabels = {
  QUEUED: "Queued",
  SUCCEEDED: "Succeeded",
  FAILED: "Failed",
  SKIPPED: "Skipped",
} as const;

export const runTriggerLabels = {
  CONVERSATION_IDLE: "The conversation went quiet",
  HANDOFF_RELEASED: "A staff handoff ended",
  MANUAL_REPROCESS: "Run again by an operator",
  EVAL: "Evaluation run",
} as const;

// The audit action is `customer_memory_fact.<event>`. The event names are the ones of the audit table
// (`docs/architecture/phase-5-memory-api.md`, section 3). An action this console does not know shows as its
// own name in words, so a new event is never hidden.
const AUDIT_EVENTS: Readonly<Record<string, string>> = {
  created: "Added by the customer",
  created_by_staff: "Added by staff",
  candidate_proposed: "Proposed by the assistant",
  auto_activated: "Activated automatically",
  activated: "Activated",
  activated_by_staff: "Activated by staff",
  candidate_rejected_by_staff: "Rejected by staff",
  candidate_accepted_by_staff: "Accepted by staff",
  confirmed: "Confirmed by the customer",
  confirmed_by_staff: "Confirmed by staff",
  disputed: "Disputed by the customer",
  disputed_by_staff: "Disputed by staff",
  dispute_resolved: "Dispute resolved",
  dispute_resolved_by_staff: "Dispute resolved by staff",
  corrected: "Corrected by the customer",
  corrected_by_staff: "Corrected by staff",
  deleted: "Deleted by the customer",
  deleted_by_staff: "Deleted by staff",
  expired: "Expired",
};

// A refused command writes its own audit row with a generic action name (`command.rejected`,
// `authorization.denied`), not a memory one. The row holds a command name and a code, never a value.
const GENERIC_AUDIT_ACTIONS: Readonly<Record<string, string>> = {
  "command.rejected": "A change was refused",
  "staff_command.rejected": "A change was refused",
  "customer_command.rejected": "A change was refused",
  "authorization.denied": "A change was refused (not allowed)",
  "customer_authorization.denied": "A change was refused (not allowed)",
};

export function auditActionLabel(action: string): string {
  const generic = GENERIC_AUDIT_ACTIONS[action];
  if (generic !== undefined) return generic;
  const prefix = "customer_memory_fact.";
  if (action.startsWith(prefix)) {
    const known = AUDIT_EVENTS[action.slice(prefix.length)];
    if (known !== undefined) return known;
  }
  return humanize(action.replaceAll(".", " ").replaceAll("_", " "));
}

/** The staff words for a conflict code whose state the page already reloaded. */
export const conflictStatusPhrase = (status: MemoryStatusValue | null): string =>
  status === null ? "" : ` It is now: ${memoryStatusLabels[status].toLowerCase()}.`;

// --- the workspace ------------------------------------------------------------------------------------

/** Active facts grouped by kind, in the order of the closed kind list. Only kinds with a fact appear. */
export function groupByKind(
  facts: readonly StaffMemoryFact[],
): readonly Readonly<{ kind: MemoryKindValue; facts: readonly StaffMemoryFact[] }>[] {
  return memoryKindValues
    .map((kind) => ({ kind, facts: facts.filter((fact) => fact.kind === kind) }))
    .filter((group) => group.facts.length > 0);
}
